// src/routes/avatar-editor/hooks/useEditorState.ts

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LOCAL_STORAGE_KEY, makeInitialState } from '../constants';
import type { WizardState } from '../types';

const AUTOSAVE_DEBOUNCE_MS = 300;

/**
 * Wizard state container with localStorage persistence + draft API.
 *
 * - `update(key, value)` patches a single field and triggers debounced autosave.
 * - `merge(partial)` applies a multi-field patch in one go.
 * - `reset()` clears the in-memory state AND removes the persisted draft.
 * - `loadDraft()` repopulates the state from localStorage; returns true if a
 *   draft existed. Callers use this after the user clicks "Resume draft".
 * - `hasDraft()` is a non-mutating probe so the entry screen can decide
 *   whether to render <DraftResumePrompt /> on mount.
 */
export function useEditorState() {
  const [state, setState] = useState<WizardState>(() => makeInitialState());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persist = useCallback((s: WizardState) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(s));
    } catch (err) {
      // localStorage may be quota-exceeded if the source photo is huge.
      // We swallow because the wizard still works in-memory; user just
      // loses resume capability.
      console.warn('[useEditorState] persist failed', err);
    }
  }, []);

  const scheduleSave = useCallback((s: WizardState) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(s), AUTOSAVE_DEBOUNCE_MS);
  }, [persist]);

  // Cleanup any pending save when the component unmounts so we don't write
  // after the route is gone.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const update = useCallback(<K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setState(prev => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const merge = useCallback((patch: Partial<WizardState>) => {
    setState(prev => {
      const next = { ...prev, ...patch };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const reset = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // Ignore — clearing localStorage shouldn't be fatal.
    }
    setState(makeInitialState());
  }, []);

  const hasDraft = useCallback((): boolean => {
    try {
      return !!localStorage.getItem(LOCAL_STORAGE_KEY);
    } catch {
      return false;
    }
  }, []);

  const loadDraft = useCallback((): boolean => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as WizardState;
      if (parsed.version !== 1) {
        // Schema bump invalidates older drafts — drop them silently.
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return false;
      }
      setState(parsed);
      return true;
    } catch (err) {
      console.warn('[useEditorState] loadDraft failed', err);
      return false;
    }
  }, []);

  return useMemo(
    () => ({ state, update, merge, reset, loadDraft, hasDraft }),
    [state, update, merge, reset, loadDraft, hasDraft],
  );
}
